import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { isDev, isLocalhost } from '../env';
import { FeatureFlagName, featureFlags } from './flags';
import { FeatureFlagContext } from './types';

type FeatureFlagOverrides = Partial<Record<FeatureFlagName, boolean>>;

const LS_KEY = 'feature_flags_override';
const canOverride = isDev() || isLocalhost;

const emptyContext = (): FeatureFlagContext => ({ userId: null, groupIds: [], salespointId: null });

function isFeatureFlagName(name: string): name is FeatureFlagName {
  return name in featureFlags;
}

function readOverrides(): FeatureFlagOverrides {
  if (!canOverride) return {};

  let saved: FeatureFlagOverrides = {};
  try {
    saved = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch {
    saved = {};
  }

  const param = new URLSearchParams(window.location.search).get('ff');
  param?.split(',').forEach(raw => {
    const name = raw.trim().replace(/^-/, '');
    if (isFeatureFlagName(name)) {
      saved[name] = !raw.trim().startsWith('-');
    }
  });

  try {
    localStorage.setItem(LS_KEY, JSON.stringify(saved));
  } catch {
    // Хранилище недоступно: переключения действуют до перезагрузки
  }

  return saved;
}

const LS_CHOICE_KEY = 'feature_flags_choice';

function readChoices(): FeatureFlagOverrides {
  try {
    return JSON.parse(localStorage.getItem(LS_CHOICE_KEY) || '{}');
  } catch {
    return {};
  }
}

export const useFeatureFlagsStore = defineStore('feature-flags', () => {
  const context = ref<FeatureFlagContext>(emptyContext());
  const overrides = ref<FeatureFlagOverrides>(readOverrides());
  const choices = ref<FeatureFlagOverrides>(readChoices());

  /** Фича доступна пользователю: по правилу из реестра или ручному включению на dev */
  const available = computed(() => {
    const result = {} as Record<FeatureFlagName, boolean>;

    (Object.keys(featureFlags) as FeatureFlagName[]).forEach(name => {
      result[name] = overrides.value[name] ?? featureFlags[name].enabled(context.value);
    });

    return result;
  });

  const flags = computed(() => {
    const result = {} as Record<FeatureFlagName, boolean>;

    (Object.keys(featureFlags) as FeatureFlagName[]).forEach(name => {
      result[name] = available.value[name] && (choices.value[name] ?? !featureFlags[name].optIn);
    });

    return result;
  });

  function setContext(value: Partial<FeatureFlagContext>) {
    context.value = { ...context.value, ...value };
  }

  function setChoice(name: FeatureFlagName, enabled: boolean) {
    choices.value = { ...choices.value, [name]: enabled };

    try {
      localStorage.setItem(LS_CHOICE_KEY, JSON.stringify(choices.value));
    } catch {
      // Хранилище недоступно: выбор действует до перезагрузки
    }
  }

  return { flags, available, context, overrides, setContext, setChoice };
});
